import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getDiagnosticsAnalyticsList } from "../../../../../Api/BmsFaultsApi";
import {
  BMS_FAULTS_META_INFO_FIELDS,
  withMetaInfoFields,
} from "../../../../../Helper/Analytics/tableMetaInfo";

const PAGE_SIZE = 20;

// The metaInfo here is a floor, not the whole request: whatever the configured
// columns need is unioned on below. Without that, adding a column to org meta
// paints a header the request never fetches a value for.
export const CHART_API_CONFIG = {
  triggeredFaultBreakdown: {
    groupBy: ["batteryID", "FaultSeverityLevelID"],
    select: ["GeneratedFaultsCount"],
    metaInfo: [
      "licensePlateNumber",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
      "pingStatus",
    ],
  },
  resolutionDurationBreakdown: {
    groupBy: ["batteryID"],
    select: [
      "ResolvedFaultCount",
      "avgResolutionDuration",
      "minResolutionDuration",
      "maxResolutionDuration",
    ],
    metaInfo: [
      "licensePlateNumber",
      "originState",
      "originCity",
      "originDepo",
      "pingStatus",
    ],
  },
};

// API returns lowercase metric type names for resolution duration; map to the
// camelCase keys expected by the orgmeta column dataIndex values.
const RESOLUTION_METRIC_MAP = {
  resolvedfaultcount: "ResolvedFaultCount",
  "resolved fault count": "ResolvedFaultCount",
  averageresolutiontime: "avgResolutionDuration",
  "average resolution time": "avgResolutionDuration",
  avgresolutionduration: "avgResolutionDuration",
  mintimetoresolve: "minResolutionDuration",
  "min time to resolve": "minResolutionDuration",
  minresolutionduration: "minResolutionDuration",
  maxtimetoresolve: "maxResolutionDuration",
  "max time to resolve": "maxResolutionDuration",
  maxresolutionduration: "maxResolutionDuration",
};

const transformRow = (chartName, item, index) => {
  if (chartName === "resolutionDurationBreakdown") {
    const row = {
      id: item.xLabel || String(index),
      xLabel: item.xLabel,
      licensePlateNumber: item.xLabel,
      ResolvedFaultCount: 0,
      avgResolutionDuration: 0,
      minResolutionDuration: 0,
      maxResolutionDuration: 0,
      ...item.metaInfo,
    };
    for (const metric of item.metricsArray || []) {
      const key =
        RESOLUTION_METRIC_MAP[(metric.type || "").toLowerCase()] || metric.type;
      row[key] = metric.value ?? 0;
    }
    return row;
  }

  // triggeredFaultBreakdown: metric.type is the severity level ID ("1"–"5");
  // map to descriptive field names matching the orgmeta dataIndex values.
  const SEVERITY_KEY_MAP = {
    "1": "severityL1",
    "2": "severityL2",
    "3": "severityL3",
    "4": "severityL4",
    "5": "severityL5",
  };

  const row = {
    id: item.xLabel || String(index),
    xLabel: item.xLabel,
    licensePlateNumber: item.xLabel,
    ...item.metaInfo,
  };
  for (const metric of item.metricsArray || []) {
    const key = SEVERITY_KEY_MAP[metric.type] ?? metric.type;
    row[key] = metric.value ?? 0;
  }
  return row;
};

const buildFilters = ({ batteryID, faultName, chartName, resolutionType, severityName }) => {
  const filters = [];

  if (batteryID) {
    filters.push({ name: "batteryID", values: [batteryID] });
  }

  if (faultName) {
    filters.push({ name: "errorDescription", values: [faultName] });
  }

  if (chartName === "triggeredFaultBreakdown" && severityName) {
    filters.push({ name: "FaultSeverityName", values: [severityName] });
  }

  if (chartName === "resolutionDurationBreakdown" && resolutionType) {
    filters.push({
      name: "ResolutionType",
      values: [resolutionType === "auto" ? "AutoResolved" : "ManualResolved"],
    });
  }

  return filters;
};

const useAnalyticsBmsFaultDrilldown = ({
  batteryID,
  faultName,
  chartName,
  resolutionType,
  severityName,
  startDate,
  endDate,
  open,
  currentTableColumns,
  columnsConfig,
}) => {
  const [tableData, setTableData] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const columnsRef = useRef(currentTableColumns);
  useEffect(() => {
    columnsRef.current = currentTableColumns;
  }, [currentTableColumns]);

  const apiConfig =
    CHART_API_CONFIG[chartName] || CHART_API_CONFIG.triggeredFaultBreakdown;

  // Memoised: it feeds buildParams, and a fresh array each render would refetch
  // on a loop.
  const metaInfoFields = useMemo(
    () => withMetaInfoFields(apiConfig.metaInfo, columnsConfig, BMS_FAULTS_META_INFO_FIELDS),
    [apiConfig, columnsConfig]
  );

  const buildParams = useCallback(
    () => ({
      startDate,
      endDate,
      groupBy: apiConfig.groupBy,
      select: apiConfig.select,
      metaInfo: metaInfoFields,
      filters: buildFilters({ batteryID, faultName, chartName, resolutionType, severityName }),
      granularity: null,
      skipPagination: false,
      pageNum: pageNumber,
      pageCount: PAGE_SIZE,
    }),
    [batteryID, faultName, chartName, resolutionType, severityName, startDate, endDate, pageNumber, apiConfig, metaInfoFields]
  );

  const fetchData = useCallback(async () => {
    if (!open || !batteryID) return;

    setIsLoading(true);
    try {
      const params = buildParams();
      const response = await getDiagnosticsAnalyticsList({}, params);
      const listData = response?.response?.data || [];
      const total = response?.response?.total?.total ?? listData.length;

      setTableData(
        listData.map((item, index) => transformRow(chartName, item, index))
      );
      setPageInfo({ total });
    } catch (err) {
      console.error("[useAnalyticsBmsFaultDrilldown] fetch error:", err);
      setTableData([]);
      setPageInfo({});
    } finally {
      setIsLoading(false);
    }
  }, [open, batteryID, buildParams, chartName]);

  useEffect(() => {
    if (!open) return;
    fetchData();
  }, [open, pageNumber]);

  useEffect(() => {
    if (!open) {
      setTableData([]);
      setPageInfo({});
      setPageNumber(1);
      setIsSelectAll(false);
      setSelectedRowKeys([]);
    }
  }, [open]);

  // ── Pagination ────────────────────────────────────────────────────────────────

  const onPageChange = useCallback((page) => {
    setPageNumber(page);
  }, []);

  // ── Row selection ─────────────────────────────────────────────────────────────

  const onSelectChange = useCallback((record, selected) => {
    setSelectedRowKeys((prev) =>
      selected ? [...prev, record.id] : prev.filter((k) => k !== record.id)
    );
    if (!selected) setIsSelectAll(false);
  }, []);

  const onSelectAllChange = useCallback((selected, selectedRows) => {
    setIsSelectAll(selected);
    setSelectedRowKeys(selected ? selectedRows.map((r) => r.id) : []);
  }, []);

  const getCurrentPageSelection = useCallback(
    () => selectedRowKeys,
    [selectedRowKeys]
  );

  const getTotalRowSelection = useCallback(
    () => selectedRowKeys,
    [selectedRowKeys]
  );

  const getTotalRowSelectionCount = useCallback(() => {
    if (isSelectAll) return pageInfo?.total ?? 0;
    return selectedRowKeys.length;
  }, [isSelectAll, pageInfo, selectedRowKeys]);

  // ── CSV (client-side — analytics rows are already aggregated) ─────────────────

  const getCSVData = useCallback(async () => {
    const cols = columnsRef.current || [];
    const rows =
      isSelectAll || !selectedRowKeys.length
        ? tableData
        : tableData.filter((row) => selectedRowKeys.includes(row.id));

    return rows.map((row) => {
      const csvRow = {};
      cols.forEach((col) => {
        csvRow[col.headerLabel] = row[col.dataIndex] ?? "";
      });
      return csvRow;
    });
  }, [tableData, isSelectAll, selectedRowKeys]);

  const getCSVFileName = useCallback(() => {
    const label =
      chartName === "triggeredFaultBreakdown"
        ? "TriggeredFaults"
        : "ResolutionDuration";
    const safeFault = (faultName || "drilldown")
      .replace(/\s+/g, "_")
      .slice(0, 40);
    return `BmsFault_${label}_${safeFault}_${new Date().toLocaleDateString(
      "en-US",
      { year: "numeric", month: "long", day: "numeric" }
    )}.csv`;
  }, [chartName, faultName]);

  const getCsvColumnParams = useCallback(
    () => ({
      columnOrder: (columnsRef.current || []).map((col) => col.headerLabel),
      columns: JSON.stringify(columnsRef.current || []),
    }),
    []
  );

  return {
    tableData,
    pageInfo,
    isLoading,
    pageNumber,
    onPageChange,
    isSelectAll,
    onSelectChange,
    onSelectAllChange,
    getTotalRowSelection,
    getTotalRowSelectionCount,
    getCurrentPageSelection,
    getCSVData,
    getCSVFileName,
    getCsvColumnParams,
  };
};

export default useAnalyticsBmsFaultDrilldown;
